import { Container, Dropdown, Typography, OutsideClickHandler } from '@cleartrip/ct-design-components'
import Rupee from '@/assets/icons/Ruppee'
import Rupee_Intl from '@/assets/icons/Ruppee_Intl'
import Bolt from '@/assets/icons/Bolt'
import Bolt_Intl from '@/assets/icons/Bolt_Intl'
import SortList from '@/assets/icons/SortList'
import SortListIntl from '@/assets/icons/SortList_Intl'
import { DownChevronNew } from '@/assets/icons/DownChevron'
import { useState } from 'react'
import { SortOptionType } from '@/types/srp'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'
import SortbarLoader from '../Loaders/SortBarLoader'
import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { ProcessedData } from '@/utils/srp/types'
import { isEmpty } from '@/utils/general/browserHelper'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'

type SortOptionsType = {
    activeSort: SortOptionType[]
    setActiveSort: (activeSort: SortOptionType[] | ((prevState: SortOptionType[]) => SortOptionType[])) => void
    isRT: boolean
    J1from: string
    J2from: string
    cheapestFilteredFlight: string
    fastestFilteredFlight?: string
    isLoading: boolean
    processedData: Partial<ProcessedData>
    initialTS?: number
}

type SortOption = {
    title: string
    sortType: 'asc' | 'desc'
    sortingBasis: string
    sortKey: string
    icon?: JSX.Element
    isSelected: boolean
    legNo?: number
    listKey?: string
}
const SortOptions: React.FC<SortOptionsType> = ({
    activeSort,
    setActiveSort,
    J1from,
    J2from,
    isRT,
    cheapestFilteredFlight,
    fastestFilteredFlight,
    isLoading,
    processedData,
    initialTS
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
    const price = 'price'
    const depart = 'depart'
    const asc = 'asc'
    const desc = 'desc'

    const [
        { sortType: onwardSortType, sortingBasis: onwardSortingBasis },
        { sortType: returnSortType, sortingBasis: returnSortingBasis }
    ] = activeSort

    const { searchData } = useAppSelector(getSrpSearchData)
    const { wowOn } = useWowPricingExperiment(!!processedData?.isIntl)
    // Show "WOW price - low to high" quick sort only when WOW is on AND:
    //   - One-way only (RT is excluded by reference behavior), AND
    //   - Either CFW (any flow) OR International (non-CFW).
    // Domestic non-CFW does NOT get this option.
    const showWowPriceSort =
        wowOn &&
        !isRT &&
        (!!processedData?.isCfw || !!processedData?.isIntl)

    const closeDropdown = () => {
        setIsDropdownOpen(false)
    }
    const toggleDropdown = (e: any) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        // Raven event for sort tab click (only when opening the dropdown)
        if (!isDropdownOpen && searchData && processedData) {
            ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
                action_name: 'sort_results_tab_click',
                action_type: 'click',
                property: 'onward'
            })
        }
        setIsDropdownOpen(prev => !prev)
    }

    const createSortOptions = ({ legNo }: { legNo: number }): SortOption[] => {
        const sortOptions: SortOption[] = []
        const from = legNo == 0 ? J1from : J2from
        const [sortType, sortBasis] =
            legNo == 0 ? [onwardSortType, onwardSortingBasis] : [returnSortType, returnSortingBasis]

        const sortVariables = ['departure_up', 'departure_down'] as const
        const sortTitleObject: Record<typeof sortVariables[number], string> = {
            departure_up: `Early Departure (${from})`,
            departure_down: `Late Departure (${from})`
        }

        const sortTypeObject: Record<typeof sortVariables[number], typeof asc | typeof desc> = {
            departure_up: asc,
            departure_down: desc
        }

        const sortBasisObject: Record<typeof sortVariables[number], string> = {
            departure_up: depart,
            departure_down: depart
        }
        const isSortSelectedObject: Record<typeof sortVariables[number], boolean> = {
            departure_up: sortType === asc && sortBasis === depart,
            departure_down: sortType === desc && sortBasis === depart
        }

        sortVariables.forEach(sortKey => {
            sortOptions.push({
                title: sortTitleObject[sortKey],
                sortType: sortTypeObject[sortKey],
                sortingBasis: sortBasisObject[sortKey],
                sortKey,
                isSelected: isSortSelectedObject[sortKey],
                listKey: `${sortKey}_${legNo}`,
                legNo
            })
        })

        return sortOptions
    }

    const createQucikSortOptions = (isIntl: boolean): SortOption[] => {
        const sortOptions: SortOption[] = []

        type QuickSortKey = 'price_up' | 'duration_up' | 'wow_price_up'

        const addQuick = (key: QuickSortKey) => {
            const titleMap: Record<QuickSortKey, string> = {
                price_up: 'Cheapest',
                duration_up: 'Fastest',
                wow_price_up: 'Wow price low to high'
            }
            const typeMap: Record<QuickSortKey, typeof asc | typeof desc> = {
                price_up: asc,
                duration_up: asc,
                wow_price_up: asc
            }
            const basisMap: Record<QuickSortKey, string> = {
                price_up: price,
                duration_up: 'duration',
                wow_price_up: 'wow_price'
            }
            const iconMap: Partial<Record<QuickSortKey, JSX.Element>> = {
                price_up: isIntl ? <Rupee_Intl /> : <Rupee />,
                duration_up: isIntl ? <Bolt_Intl /> : <Bolt />,
                wow_price_up: isIntl ? <Rupee_Intl /> : <Rupee />
            }
            const isSelectedMap: Record<QuickSortKey, boolean> = {
                price_up:
                    onwardSortType === returnSortType &&
                    onwardSortingBasis === returnSortingBasis &&
                    onwardSortType === asc &&
                    onwardSortingBasis === price,
                duration_up: !isRT && onwardSortType === asc && onwardSortingBasis === 'duration',
                wow_price_up:
                    onwardSortType === asc && onwardSortingBasis === 'wow_price'
            }
            sortOptions.push({
                title: titleMap[key],
                sortType: typeMap[key],
                sortingBasis: basisMap[key],
                sortKey: key,
                icon: iconMap[key],
                isSelected: isSelectedMap[key]
            })
        }

        // Always include Cheapest
        addQuick('price_up')
        // CFW + WOW: include WOW price (low to high) as a quick option
        if (showWowPriceSort) {
            addQuick('wow_price_up')
        }
        // For International One-Way and non-CFW, include Fastest as a second quick card
        if (!!processedData?.isIntl && !isRT && !processedData?.isCfw) {
            addQuick('duration_up')
        }

        return sortOptions
    }

    const onwardSortOptions = createSortOptions({ legNo: 0 })
    const returnSortOptions = isRT ? createSortOptions({ legNo: 1 }) : []
    const journeySortOptions = [...onwardSortOptions, ...returnSortOptions]
    const quickSortOptions = createQucikSortOptions(processedData?.isIntl || false)

    const getSortOptionsList = () => {
        const sortOptions = journeySortOptions.map(sortOption => {
            const { title, listKey = '' } = sortOption
            return {
                key: listKey,
                label: title
            }
        })
        return sortOptions
    }

    const selectQuickSort = (sortType: string) => {
        switch (sortType) {
            case price:
                setActiveSort([
                    { title: 'Cheapest', sortType: 'asc', sortingBasis: 'price', sortKey: 'price_up' },
                    { title: 'Cheapest', sortType: 'asc', sortingBasis: 'price', sortKey: 'price_up' }
                ])
                ravenSDKSrpCfwTrigger('a_sort_applied', searchData, processedData, {
                    filter_value: '',
                    filter_type: 'Cheapest'
                })
                break
            case 'duration':
                // Intl OW quick sort: Fastest
                setActiveSort([
                    { title: 'Fastest', sortType: 'asc', sortingBasis: 'duration', sortKey: 'duration_up' },
                    { title: 'Fastest', sortType: 'asc', sortingBasis: 'duration', sortKey: 'duration_up' }
                ])
                break
            case 'wow_price':
                // CFW + WOW: WOW price ascending
                setActiveSort([
                    { title: 'Wow price low to high', sortType: 'asc', sortingBasis: 'wow_price', sortKey: 'wow_price_up' },
                    { title: 'Wow price low to high', sortType: 'asc', sortingBasis: 'wow_price', sortKey: 'wow_price_up' }
                ])
                ravenSDKSrpCfwTrigger('a_sort_applied', searchData, processedData, {
                    filter_value: '',
                    filter_type: 'WOW price - low to high'
                })
                break
        }
    }
    const selectSortOption = (key: string) => {
        const sortOption = journeySortOptions.find(option => option.listKey === key)
        if (!sortOption) return
        const { legNo, title, sortType, sortingBasis, sortKey } = sortOption || {}
        const updatedSort = {
            title: title,
            sortType: sortType,
            sortingBasis: sortingBasis,
            sortKey: sortKey
        }
        
        // Raven event for sort selection (trigger before UI state changes)
        if (searchData && processedData) {
            // Determine filter_type based on sortingBasis
            let filterType = 'price_sort' // default
            if (sortingBasis?.includes('duration') || sortKey?.includes('duration')) {
                filterType = 'duration_sort'
            } else if (sortingBasis?.includes('departure') || sortKey?.includes('depart')) {
                filterType = 'departure_sort'
            } else if (sortingBasis?.includes('arrival') || sortKey?.includes('arrival')) {
                filterType = 'arrival_sort'
            }
            
            ravenSDKSrpCfwTrigger('a_sort_applied', searchData, processedData, {
                filter_type: filterType,
                filter_value: title,
                a_is_sort_applied: 'yes',
                a_quick_sort: 'no',
                a_nearby_flights: '0',
                page_loadtime: initialTS ? new Date().getTime() - initialTS : 'N/A'
            })
        }
        
        setActiveSort(prevSort => prevSort.map((sort, index) => (index === legNo ? updatedSort : sort)))
    }

    const getSelectedSortOptions = () => {
        const selectedSortOptions: string[] = []
        const selectedSortData: SortOption[] = []
        journeySortOptions.forEach(journeySortOption => {
            const isSelected = !isEmpty(
                activeSort.find((activeSortOption, index) => {
                    const { legNo } = journeySortOption
                    return legNo === index && activeSortOption.sortKey === journeySortOption.sortKey
                })
            )
            if (isSelected && journeySortOption?.listKey) {
                selectedSortOptions.push(journeySortOption.listKey)
                selectedSortData.push(journeySortOption)
            }
        })
        return { selectedSortOptions, selectedSortData }
    }

    if (isLoading) {
        return <SortbarLoader />
    }

    let { selectedSortOptions = [], selectedSortData = [] } = getSelectedSortOptions()

    return (
        <Container className="flex flex-between">
            <Container className="flex flex-middle" style={{ gap: '10px' }}>
                {quickSortOptions.map(quickSortOption => {
                    const { sortingBasis, title, icon, isSelected } = quickSortOption
                    return (
                        <>
                            <Container
                                className="br-12 py-2 px-4 ws-nowrap flex flex-middle c-pointer"
                                css={{
                                    border: isSelected ? 'solid 2px black' : 'solid 1px #E6E6E6',
                                    height: '60px'
                                }}
                                backgroundColor={isSelected ? '#F3F3F3' : ''}
                                onClick={() => {
                                    if (!isSelected) {
                                        selectQuickSort(sortingBasis)
                                    }
                                }}
                                width={'221px'}
                            >
                                <Container className="mr-2" height={processedData?.isIntl ? '32px' : '24px'}>
                                    {icon}
                                </Container>
                                <Container>
                                    <Typography variant="HM4" className="c-pointer" >
                                        {title}
                                    </Typography>
                                    <Typography variant="B2" color="subheading" className="c-pointer">
                                        {title === 'Fastest' && fastestFilteredFlight ? fastestFilteredFlight : cheapestFilteredFlight}
                                    </Typography>
                                </Container>
                            </Container>
                        </>
                    )
                })}
            </Container>

            <Container className="p-relative">
                <Container
                    className={`br-12 ws-nowrap flex flex-middle c-pointer ${processedData?.isIntl ? 'p-3' : 'px-4 pt-2 pb-3'}`} 
                    css={{
                        border: !isEmpty(selectedSortData) ? 'solid 2px black' : 'solid 1px #E6E6E6',
                        backgroundColor: !isEmpty(selectedSortData) ? '#F3F3F3' : ''
                    }}
                    onClick={toggleDropdown}
                >
                    <Container className='flex flex-middle c-pointer' style={{ height: '38px' }}>
                        <Container className="mr-2" height={processedData?.isIntl ? '32px' : '24px'}>
                         {processedData?.isIntl ? <SortListIntl /> : <SortList />}
                        </Container>
                        <Container>
                            <Typography variant="HM4" className="c-pointer pr-3" >
                                Sort Options
                            </Typography>
                            {!isRT && !isEmpty(activeSort) && selectedSortData?.length === 1 && (
                                <Typography variant="B3" color="subheading" className="c-pointer">
                                    {selectedSortData[0].title?.split?.(' (')?.[0] || ''}
                                </Typography>
                            )}
                        </Container>
                        <DownChevronNew
                            style={{
                                transform: isDropdownOpen ? 'rotate(-180deg)' : 'rotate(0deg)'
                            }}
                        />
                    </Container>
                </Container>

                {isDropdownOpen ? (
                    <OutsideClickHandler onOutsideClick={closeDropdown}>
                        <Container className="p-absolute l-0" css={{ minWidth: '247px', zIndex: 30 }}>
                            <Dropdown
                                optionList={getSortOptionsList()}
                                onClick={(key: string) => {
                                    selectSortOption(key)
                                    closeDropdown()
                                }}
                                styleConfig={{
                                    listDropdown: {
                                        labelContainer: {
                                            className: 'c-pointer'
                                        },
                                        labelTypography: {
                                            className: 'c-pointer'
                                        }
                                    }
                                }}
                                multiSelect={true}
                                selectedOption={selectedSortOptions}
                            />
                        </Container>
                    </OutsideClickHandler>
                ) : (
                    ''
                )}
            </Container>
        </Container>
    )
}

export default SortOptions
